import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Save, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page-header';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useSaveWorkflow, useWorkflows } from '@/hooks/queries/useWorkflows';
import { useRole } from '@/hooks/useRole';
import { formatDA } from '@/lib/format';
import { t } from '@/locales/i18n';
import type { RoleType, TypeFacture, WorkflowConfig } from '@/lib/database.types';

const ROLES: { value: RoleType; label: string }[] = [
  { value: 'super_admin', label: 'Super admin' },
  { value: 'direction', label: 'Direction' },
  { value: 'responsable_activite', label: "Resp. d'activité" },
  { value: 'comptable', label: 'Comptable' },
  { value: 'commercial', label: 'Commercial' },
];

const DEFAULT_CONFIG: WorkflowConfig = {
  tranches: [
    { max: 100000, approbateurs: ['responsable_activite'] },
    { max: 500000, approbateurs: ['direction'] },
    { max: null, approbateurs: ['direction', 'super_admin'] },
  ],
};

export function WorkflowsPage(): JSX.Element {
  const { isAdmin } = useRole();
  const { data: entreprises = [] } = useEntreprises();
  const [entrepriseId, setEntrepriseId] = useState('');

  useEffect(() => {
    if (!entrepriseId && entreprises[0]) setEntrepriseId(entreprises[0].id);
  }, [entreprises, entrepriseId]);

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t('parametres.workflows.title')}
        subtitle={t('parametres.workflows.subtitle')}
      />
      <div className="space-y-6 p-6">
        <div className="flex items-center gap-3">
          <label className="text-sm text-muted-foreground">Entreprise</label>
          <select
            value={entrepriseId}
            onChange={(e) => setEntrepriseId(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          >
            {entreprises.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nom}
              </option>
            ))}
          </select>
        </div>

        {entrepriseId && (
          <div className="grid gap-4 lg:grid-cols-2">
            <WorkflowEditor
              entrepriseId={entrepriseId}
              type="client"
              canEdit={isAdmin}
              title={t('parametres.workflows.client')}
            />
            <WorkflowEditor
              entrepriseId={entrepriseId}
              type="fournisseur"
              canEdit={isAdmin}
              title={t('parametres.workflows.fournisseur')}
            />
          </div>
        )}
      </div>
    </div>
  );
}

interface EditorProps {
  entrepriseId: string;
  type: TypeFacture;
  title: string;
  canEdit: boolean;
}

function WorkflowEditor({ entrepriseId, type, title, canEdit }: EditorProps): JSX.Element {
  const { data: rows = [], isLoading } = useWorkflows(entrepriseId);
  const save = useSaveWorkflow();
  const existing = rows.find((r) => r.type_facture === type);
  const [config, setConfig] = useState<WorkflowConfig>(DEFAULT_CONFIG);

  useEffect(() => {
    if (existing) setConfig(existing.config);
    else setConfig(DEFAULT_CONFIG);
  }, [existing]);

  const updateTranche = (i: number, patch: Partial<WorkflowConfig['tranches'][number]>): void => {
    setConfig((c) => ({
      ...c,
      tranches: c.tranches.map((tr, idx) => (idx === i ? { ...tr, ...patch } : tr)),
    }));
  };

  const addTranche = (): void => {
    setConfig((c) => ({
      ...c,
      tranches: [...c.tranches, { max: null, approbateurs: ['direction'] }],
    }));
  };

  const removeTranche = (i: number): void => {
    setConfig((c) => ({ ...c, tranches: c.tranches.filter((_, idx) => idx !== i) }));
  };

  const toggleRole = (i: number, role: RoleType): void => {
    const current = config.tranches[i]?.approbateurs ?? [];
    const next = current.includes(role) ? current.filter((r) => r !== role) : [...current, role];
    updateTranche(i, { approbateurs: next });
  };

  return (
    <article className="space-y-3 rounded-lg border border-border bg-card p-4">
      <header className="flex items-center justify-between">
        <h3 className="font-semibold">{title}</h3>
        <Badge variant="outline">{type}</Badge>
      </header>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}
        </div>
      ) : (
        <div className="space-y-3">
          {config.tranches.map((tr, i) => (
            <div key={i} className="rounded-md border border-border bg-muted/20 p-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {t('parametres.workflows.tranche')}
                </span>
                <Input
                  type="number"
                  value={tr.max ?? ''}
                  onChange={(e) =>
                    updateTranche(i, { max: e.target.value === '' ? null : Number(e.target.value) })
                  }
                  className="h-8 w-32"
                  placeholder={t('parametres.workflows.noLimit')}
                  disabled={!canEdit}
                />
                <span className="text-xs text-muted-foreground">
                  DA · résultat ={' '}
                  {tr.max === null ? t('parametres.workflows.noLimit') : formatDA(tr.max)}
                </span>
                {canEdit && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-auto h-7 w-7 text-destructive"
                    onClick={() => removeTranche(i)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              <div className="mt-2">
                <div className="text-xs text-muted-foreground">
                  {t('parametres.workflows.approbateurs')}
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {ROLES.map((r) => {
                    const active = tr.approbateurs.includes(r.value);
                    return (
                      <button
                        key={r.value}
                        type="button"
                        onClick={() => canEdit && toggleRole(i, r.value)}
                        disabled={!canEdit}
                        className={
                          'rounded-md border px-2 py-1 text-xs transition-colors ' +
                          (active
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-background text-muted-foreground hover:text-foreground')
                        }
                      >
                        {r.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
          {canEdit && (
            <div className="flex items-center justify-between">
              <Button variant="outline" size="sm" onClick={addTranche}>
                <Plus className="h-4 w-4" /> {t('parametres.workflows.addTranche')}
              </Button>
              <Button
                size="sm"
                disabled={save.isPending}
                onClick={async () => {
                  await save.mutateAsync({
                    entreprise_id: entrepriseId,
                    type_facture: type,
                    config,
                  });
                  toast.success('Workflow enregistré');
                }}
              >
                <Save className="h-4 w-4" /> {t('parametres.workflows.save')}
              </Button>
            </div>
          )}
        </div>
      )}
    </article>
  );
}
